import React from "react";
import { useRouteMatch } from "react-router-dom";

import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { Collapse, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceIcon } from "assets/material-custom-icons";
import { Routes } from "constants/routes";
import { IFolder } from "store/types";

import { useDealWorkspace } from "../context/DealWorkspace-context";
import { useWorkspaceDrop } from "../Hooks/use-workspace-dnd";
import { VirtualizedFolderList } from "./VirtualizedFolderList";

const chevronColor = "#0A3142";
const iconColor = "#0A3142";
const labelColor = "#20293C";
const HEADER_HEIGHT = 36;

interface WorkspaceHeaderProps {
  isSelected: boolean;
  isWorkspaceFolderOpen: boolean;
  onWorkspaceArrowClick: () => void;
  onWorkspaceFolderClick: () => void;
  workspaceFolders: IFolder[];
}

const WorkspaceHeader: React.FC<WorkspaceHeaderProps> = ({
  isSelected,
  isWorkspaceFolderOpen,
  onWorkspaceArrowClick,
  onWorkspaceFolderClick,
  workspaceFolders
}) => {
  const { isDragOver, onDragOver, onDragLeave, onDrop } = useWorkspaceDrop();

  return (
    <Stack
      direction="row"
      alignItems="center"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{
        margin: "2px",
        height: "36px",
        padding: "0 5px",
        borderRadius: "20px",
        cursor: "pointer",
        backgroundColor: isDragOver ? "#C8DCF0" : isSelected ? "#DBE8F5" : "transparent",
        outline: isDragOver ? "2px dashed #5B9BD5" : "none",
        outlineOffset: "-2px",
        "&:hover": {
          backgroundColor: isDragOver ? "#C8DCF0" : "#ECF1F8"
        }
      }}
      gap="5px"
    >
      {workspaceFolders.length ? (
        <IconButton onClick={onWorkspaceArrowClick}>
          <ExpandMoreOutlinedIcon
            sx={{
              fill: chevronColor,
              fontSize: 20,
              transform: isWorkspaceFolderOpen ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease"
            }}
          />
        </IconButton>
      ) : (
        <Stack sx={{ width: 30, height: 20 }} />
      )}
      <Stack
        direction="row"
        alignItems="center"
        gap="5px"
        onClick={onWorkspaceFolderClick}
        sx={{
          flex: 1,
          minWidth: 0,
          cursor: "pointer"
        }}
      >
        <WorkspaceIcon sx={{ fill: iconColor, fontSize: 20 }} />
        <Typography
          sx={{
            color: labelColor,
            fontWeight: 700,
            fontSize: "16px"
          }}
        >
          Workspace
        </Typography>
      </Stack>
    </Stack>
  );
};

interface Props {
  availableHeight?: number;
}

export const Workspace: React.FC<Props> = ({ availableHeight = 0 }) => {
  const { onWorkspaceFolderClick, onWorkspaceArrowClick, isWorkspaceFolderOpen, workspaceFolders } = useDealWorkspace();
  const workspaceRootMatch = useRouteMatch({ path: Routes.dealWorkspace, exact: true });
  const isSelected = !!workspaceRootMatch;

  return (
    <Stack
      sx={{
        width: "100%",
        flexDirection: "column",
        flexShrink: 0
      }}
    >
      <WorkspaceHeader
        isSelected={isSelected}
        isWorkspaceFolderOpen={isWorkspaceFolderOpen}
        onWorkspaceArrowClick={onWorkspaceArrowClick}
        onWorkspaceFolderClick={onWorkspaceFolderClick}
        workspaceFolders={workspaceFolders}
      />
      <Collapse in={isWorkspaceFolderOpen} timeout={200} unmountOnExit>
        <VirtualizedFolderList maxHeight={Math.max(0, availableHeight - HEADER_HEIGHT)} folderList={workspaceFolders} />
      </Collapse>
    </Stack>
  );
};
